// components/artifacts/panel/ArtifactTabs.tsx
import React from "react";
import { X, Loader2, Code, BarChart2, Table, Database, Navigation, Link as LinkIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ArtifactItem, ArtifactLoadingPlaceholder, ArtifactType } from "@/types/Artifact";

interface ArtifactTabsProps {
  items: (ArtifactItem | ArtifactLoadingPlaceholder)[];
  activeId: string | null;
  onTabClick: (id: string) => void;
  onRemoveItem: (item: ArtifactItem | ArtifactLoadingPlaceholder) => void;
}

const getTypeIcon = (type: ArtifactType): React.ReactNode => {
  const iconMap: Record<ArtifactType, React.ReactNode> = {
    code: <Code className="h-4 w-4" />,
    graph: <BarChart2 className="h-4 w-4" />,
    table: <Table className="h-4 w-4" />,
    sql: <Database className="h-4 w-4" />,
    visualization: <BarChart2 className="h-4 w-4" />,
    map: <Navigation className="h-4 w-4" />,
    link: <LinkIcon className="h-4 w-4" />,
  };
  return iconMap[type];
};

export const ArtifactTabs: React.FC<ArtifactTabsProps> = React.memo(({
  items,
  activeId,
  onTabClick,
  onRemoveItem,
}) => {
  const [failedFavicons, setFailedFavicons] = React.useState<Set<string>>(new Set());

  const handleFaviconError = (itemId: string) => {
    setFailedFavicons(prev => new Set(prev).add(itemId));
  };

  return (
    <div className="flex overflow-x-auto border-b px-4">
      {items.map((item) => {
        const isLoading = 'isLoading' in item && item.isLoading;
        const isActive = activeId === item.id;
        
        // Check if it's a link artifact with a favicon
        const isLinkArtifact = item.type === 'link' && 'content' in item;
        const faviconUrl = isLinkArtifact && typeof item.content === 'object' && 'favicon' in item.content 
          ? item.content.favicon 
          : null;
        
        const showFavicon = faviconUrl && !failedFavicons.has(item.id);
        
        return (
          <TooltipProvider key={item.id}>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <div
                  onClick={() => onTabClick(item.id)}
                  className={cn(
                    "group flex cursor-pointer items-center whitespace-nowrap px-4 py-2 text-sm",
                    isActive
                      ? "border-b-2 border-primary font-medium"
                      : "text-muted-foreground hover:text-foreground",
                    isLoading && "animate-pulse"
                  )}
                >
                  {/* Show favicon for link artifacts, otherwise show type icon */}
                  {showFavicon ? (
                    <img 
                      src={faviconUrl} 
                      alt="" 
                      className="h-4 w-4 rounded-sm"
                      onError={() => handleFaviconError(item.id)}
                    />
                  ) : (
                    getTypeIcon(item.type)
                  )}
                  <span className="ml-2 max-w-[150px] truncate">
                    {item.title}
                  </span>
                  {isLoading && (
                    <Loader2 className="ml-2 h-3 w-3 animate-spin" />
                  )}

                  {/* Remove button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveItem(item);
                    }}
                    className="ml-2 rounded p-0.5 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive"
                    aria-label={`Remove ${item.title}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{item.title} {isLoading && "(Generating...)"}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}
    </div>
  );
});